import { buildConventionalScopeBody, ConventionalScope, conventionalScopeKey, coreApi, BlueprintFamilyTrendsWindowDays } from '../core'
import type { BlueprintFamilyDurationDistributionApiItem } from './durationDistribution'

// Response shape for POST /blueprint-families/duration-distribution/event-types: the
// per-family buckets, wrapped per event-type id. The histogram is type-scoped — every
// game running any event of the type, whether or not its blueprint was catalogued into a
// family — so it reconciles with `adoptionRate` on the matching rollup row.
//
// The same overlap caveat as the per-family endpoint applies within a row: a game counts
// in EVERY bucket it ran an event in, so the buckets are not a partition and their counts
// must not be summed into a denominator. See `durationDistribution.ts`.
export type BlueprintFamilyEventTypeDurationDistributionApiItem = {
  id: string
  buckets: BlueprintFamilyDurationDistributionApiItem[]
}

// Query args for the batched event-type histogram. `eventTypeIds` accepts 1–10 ids.
//
// The id field name, not a `groupBy` param, selects the endpoint: the BE checks each id
// against the kind of thing the field promises, so an event-group id sent here returns a
// 400 naming the offending field rather than silently bucketing the wrong entity.
type GetBlueprintFamilyEventTypeDurationDistributionQueryParams = {
  eventTypeIds: string[]
  windowDays?: BlueprintFamilyTrendsWindowDays
} & ConventionalScope

const eventTypeDurationDistributionApi = coreApi.injectEndpoints({
  endpoints: (builder) => ({
    getBlueprintFamilyEventTypeDurationDistribution: builder.query<
      BlueprintFamilyEventTypeDurationDistributionApiItem[],
      GetBlueprintFamilyEventTypeDurationDistributionQueryParams
    >({
      // POST-with-body modeled as a query (read-only fan-out), mirroring the sibling
      // trends endpoints.
      query: ({ eventTypeIds, windowDays, ...scope }) => ({
        url: 'blueprint-families/duration-distribution/event-types',
        method: 'POST',
        params: {
          ...(windowDays !== undefined ? { windowDays } : {}),
        },
        body: { eventTypeIds, ...buildConventionalScopeBody(scope) },
      }),
      providesTags: (_result, _err, { eventTypeIds, windowDays, ...scope }) => [
        {
          type: 'BlueprintFamilies',
          id: `duration-distribution/event-types/${[...eventTypeIds].sort().join(',')}/${windowDays ?? 'default'}/${conventionalScopeKey(scope)}`,
        },
      ],
    }),
  }),
})

export const { useGetBlueprintFamilyEventTypeDurationDistributionQuery } = eventTypeDurationDistributionApi
